import React from "react";
import { Button } from 'primereact/button';
import { InputText } from "primereact/inputtext";
import { classNames } from 'primereact/utils';
import { Password } from 'primereact/password';
import { Divider } from 'primereact/divider';
import { RadioButton } from "primereact/radiobutton";
import {
    Form,
    redirect,
    useSubmit,
    useLoaderData,
} from "react-router-dom";
import { useFormik } from 'formik';
import { update, getAccount } from "../data/accounts";
import { send, notify } from "../services/NotificationService";

export async function loader({ params }) {
    const acc = await getAccount();

    return { acc };
}

export async function action({ request, params }) {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);

    let rt;
    try {
        rt = await update(data);
    } catch (error) {
        send("warning", error.message || "Erreur lors de l'enregistrement du compte.")
        notify();
        return null;
    }

    if (rt && (rt.status === 400 || rt.Status === 400 || rt.status === 500 || rt.Status === 500)) {
        send("warning", rt.message || rt.Message || "Erreur lors de l'enregistrement du compte.")
        notify();
        return null;
    }

    send("success", "Modifications effectuées !")
    return redirect(`/moncompte`);
}

export default function Account() {
    const { acc } = useLoaderData();
    const submit = useSubmit();
    const header = <div className="font-bold mb-3">Choisir un mot de passe</div>;
    const footer = (
        <>
            <Divider />
            <ul className="pl-2 ml-2 mt-0 line-height-3">
                <li>Au moins une minuscule</li>
                <li>Au moins une majuscule</li>
                <li>Au moins un chiffre</li>
                <li>Au moins un caractère non-alphanumérique</li>
                <li>Minimum 6 caractères</li>
            </ul>
        </>
    );

    const formik = useFormik({
        initialValues: {
            email: acc?.email ?? '',
            currentPwd: '',
            newPwd: '',
            confirmPwd: '',
            address: acc?.address ?? '',
            zipcode: acc?.zipcode ?? '',
            city: acc?.city ?? '',
            phone: acc?.phone ?? '',
            fname: acc?.firstname ?? '',
            lname: acc?.lastname ?? '',
            isProfessional: acc?.isProfessional ?? false,
            companyName: acc?.companyName ?? '',
            siret: acc?.siret ?? '',
            vatNumber: acc?.vatNumber ?? ''
        },
        validate: (data) => {
            let errors = {};

            if (data.newPwd && data.newPwd !== data.confirmPwd) {
                errors.newPwd = 'Les mots de passe ne correspondent pas';
            }
            if (data.isProfessional === true || data.isProfessional === "true") {
                if (!data.companyName) {
                    errors.companyName = 'Raison sociale obligatoire';
                }
                if (!data.siret) {
                    errors.siret = 'SIRET obligatoire';
                }
            }

            return errors;
        },
        onSubmit: async (data) => {
            submit(data, { method: "post" })
        }
    });

    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const isProfessionalAccount = formik.values.isProfessional === true || formik.values.isProfessional === "true";
    const fullName = `${formik.values.fname || ''} ${formik.values.lname || ''}`.trim() || 'Mon compte';
    const stripeStatusLabel = {
        NeedToCreateAccount: 'Paiements non activés',
        NeedToCompleteAccount: 'Compte Stripe à compléter',
        AccountCreated: 'Paiements activés'
    }[acc?.stripeStatus] || 'Non configuré';
    const stripeButtonLabel = {
        NeedToCreateAccount: 'Activer les paiements',
        NeedToCompleteAccount: 'Compléter mon compte Stripe',
        AccountCreated: 'Mettre à jour Stripe'
    }[acc?.stripeStatus];

    return (
        <div className="account-page">
            <div className="account-page-header">
                <div>
                    <p className="account-page-kicker">Paramètres</p>
                    <h1>Mon compte</h1>
                    <p>{fullName}</p>
                </div>
                <div className="account-status-card">
                    <span>Type de compte</span>
                    <b>{isProfessionalAccount ? 'Professionnel' : 'Particulier'}</b>
                </div>
            </div>

            <Form method="post" id="thumbnail-form" className="account-form" onSubmit={formik.handleSubmit}>
                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-user" aria-hidden="true"></i>
                        <div>
                            <h2>Identité</h2>
                            <p>Informations visibles sur vos contrats et échanges locataires.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--two">
                        <span className="p-float-label">
                            <InputText id="fname" name="fname" value={formik.values.fname} onChange={(e) => formik.setFieldValue('fname', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('fname') })} />
                            <label htmlFor="fname">Prénom</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="lname" name="lname" value={formik.values.lname} onChange={(e) => formik.setFieldValue('lname', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('lname') })} />
                            <label htmlFor="lname">Nom</label>
                        </span>

                        <span className="p-float-label account-field-wide">
                            <InputText disabled id="email" name="email" value={formik.values.email} onChange={(e) => formik.setFieldValue('email', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('email') })} />
                            <label htmlFor="email">Email</label>
                        </span>
                    </div>
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-briefcase" aria-hidden="true"></i>
                        <div>
                            <h2>Type de compte</h2>
                            <p>Choisissez le cadre adapté à votre activité de location.</p>
                        </div>
                    </div>
                    <div className="account-choice-grid">
                        <label className={classNames('account-choice', { 'account-choice--selected': !isProfessionalAccount })} htmlFor="account-type-private">
                            <RadioButton inputId="account-type-private" name="isProfessional" value={false} onChange={(e) => formik.setFieldValue('isProfessional', e.value)} checked={!isProfessionalAccount} />
                            <span>
                                <b>Particulier</b>
                                <small>Pour louer votre matériel occasionnellement.</small>
                            </span>
                        </label>
                        <label className={classNames('account-choice', { 'account-choice--selected': isProfessionalAccount })} htmlFor="account-type-professional">
                            <RadioButton inputId="account-type-professional" name="isProfessional" value={true} onChange={(e) => formik.setFieldValue('isProfessional', e.value)} checked={isProfessionalAccount} />
                            <span>
                                <b>Professionnel</b>
                                <small>Pour une activité déclarée avec informations d'entreprise.</small>
                            </span>
                        </label>
                    </div>

                    {isProfessionalAccount && (
                        <div className="account-fields-grid account-fields-grid--three">
                            <span className="p-float-label">
                                <InputText id="companyName" name="companyName" value={formik.values.companyName} onChange={(e) => formik.setFieldValue('companyName', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('companyName') })} />
                                <label htmlFor="companyName">Raison sociale</label>
                                {isFormFieldInvalid('companyName') && <small className="p-error">{formik.errors.companyName}</small>}
                            </span>
                            <span className="p-float-label">
                                <InputText id="siret" name="siret" value={formik.values.siret} onChange={(e) => formik.setFieldValue('siret', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('siret') })} />
                                <label htmlFor="siret">SIRET</label>
                                {isFormFieldInvalid('siret') && <small className="p-error">{formik.errors.siret}</small>}
                            </span>
                            <span className="p-float-label">
                                <InputText id="vatNumber" name="vatNumber" value={formik.values.vatNumber} onChange={(e) => formik.setFieldValue('vatNumber', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('vatNumber') })} />
                                <label htmlFor="vatNumber">TVA intracommunautaire</label>
                            </span>
                        </div>
                    )}
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-map-marker" aria-hidden="true"></i>
                        <div>
                            <h2>Adresse et contact</h2>
                            <p>Coordonnées reprises dans vos documents de location.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <span className="p-float-label account-field-wide">
                            <InputText id="address" name="address" value={formik.values.address} onChange={(e) => formik.setFieldValue('address', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('address') })} />
                            <label htmlFor="address">Numéro et rue</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="zipcode" name="zipcode" value={formik.values.zipcode} onChange={(e) => formik.setFieldValue('zipcode', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('zipcode') })} />
                            <label htmlFor="zipcode">Code postal</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="city" name="city" value={formik.values.city} onChange={(e) => formik.setFieldValue('city', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('city') })} />
                            <label htmlFor="city">Ville</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="phone" name="phone" value={formik.values.phone} onChange={(e) => formik.setFieldValue('phone', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('phone') })} />
                            <label htmlFor="phone">Telephone</label>
                        </span>
                    </div>
                </section>

                <section className="account-section">
                    <div className="account-section-header">
                        <i className="pi pi-lock" aria-hidden="true"></i>
                        <div>
                            <h2>Mot de passe</h2>
                            <p>Laissez ces champs vides si vous ne souhaitez pas le modifier.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <span className="p-float-label">
                            <Password id="currentPwd" name="currentPwd" value={formik.values.currentPwd} feedback={false} onChange={(e) => formik.setFieldValue('currentPwd', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('currentPwd') })} />
                            <label htmlFor="currentPwd">Mot de passe actuel</label>
                        </span>
                        <span className="p-float-label">
                            <Password id="newPwd" name="newPwd" value={formik.values.newPwd} header={header} footer={footer} promptLabel=" " weakLabel="Trop simple" mediumLabel="Moyennement complexe" strongLabel="Très complexe" onChange={(e) => formik.setFieldValue('newPwd', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('newPwd') })} />
                            <label htmlFor="newPwd">Nouveau mot de passe</label>
                            {isFormFieldInvalid('newPwd') && <small className="p-error">{formik.errors.newPwd}</small>}
                        </span>
                        <span className="p-float-label">
                            <Password id="confirmPwd" name="confirmPwd" value={formik.values.confirmPwd} feedback={false} onChange={(e) => formik.setFieldValue('confirmPwd', e.target.value)} className={classNames({ 'p-invalid': isFormFieldInvalid('confirmPwd') })} />
                            <label htmlFor="confirmPwd">Confirmation</label>
                        </span>
                    </div>
                </section>

                {acc.stripeLink && (
                    <section className="account-section account-payment-section">
                        <div className="account-section-header">
                            <i className="pi pi-credit-card" aria-hidden="true"></i>
                            <div>
                                <h2>Paiements et cautions CB</h2>
                                <p>Connexion Stripe pour recevoir des paiements et accepter des cautions.</p>
                            </div>
                        </div>
                        <div className="account-payment-row">
                            <div>
                                <span className={classNames('account-stripe-badge', { 'account-stripe-badge--active': acc.stripeStatus === "AccountCreated" })}>{stripeStatusLabel}</span>
                            </div>
                            {stripeButtonLabel && (
                                <a className="account-stripe-link" href={acc.stripeLink}>
                                    <i className="pi pi-external-link" aria-hidden="true"></i>
                                    {stripeButtonLabel}
                                </a>
                            )}
                        </div>
                    </section>
                )}

                <div className="account-submit-bar">
                    <span>Les modifications seront appliquées à vos prochaines locations.</span>
                    <Button type="submit" icon="pi pi-save" label="Enregistrer" />
                </div>
            </Form>
        </div>
    );
}
